import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase-config';

const NewsDetail = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const newsRef = doc(db, 'news', id);
        const newsSnap = await getDoc(newsRef);
        
        if (newsSnap.exists()) {
          const data = { id: newsSnap.id, ...newsSnap.data() };
          setNews(data);

          // Load related news
          if (data.category) {
            const q = query(
              collection(db, 'news'),
              where('category', '==', data.category),
              orderBy('createdAt', 'desc'),
              limit(4)
            );
            const relatedSnap = await getDocs(q);
            const relatedData = [];
            relatedSnap.forEach(doc => {
              if (doc.id !== id) relatedData.push({ id: doc.id, ...doc.data() });
            });
            setRelated(relatedData);
          }
        }
      } catch (error) {
        console.error('Error loading news:', error);
      }
      setLoading(false);
    };
    loadData();
  }, [id]);

  // Helper function to get image URL
  const getImageUrl = (newsItem) => {
    if (!newsItem) return null;
    // Try different field names that might contain the image
    return newsItem.imageUrl || newsItem.image || newsItem.featuredImage || newsItem.banner || null;
  };

  // Helper function to get fallback image
  const getFallbackImage = () => {
    return 'https://images.unsplash.com/photo-1562774053-701939374585?w=800';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ width: '50px', height: '50px', borderRadius: '50%', border: '4px solid rgba(14,165,233,0.1)', borderTopColor: 'var(--clr-blue)', animation: 'spin 0.8s linear infinite' }}></div>
      </div>
    );
  }

  if (!news) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 0' }}>
        <i className="fas fa-exclamation-circle" style={{ fontSize: '3rem', color: 'var(--clr-rose)' }}></i>
        <h2 style={{ marginTop: '20px' }}>News not found</h2>
        <Link to="/news" style={{ display: 'inline-block', marginTop: '20px', padding: '12px 28px', background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)', borderRadius: '40px', color: 'white' }}>Back to News</Link>
      </div>
    );
  }

  const date = news.createdAt?.toDate ? news.createdAt.toDate() : new Date();
  const imageUrl = getImageUrl(news) || getFallbackImage();

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/news" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '30px', padding: '12px 24px', background: 'rgba(255,255,255,0.4)', borderRadius: '50px', transition: 'all 0.3s ease' }}>
          <i className="fas fa-arrow-left"></i> Back to News
        </Link>

        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1px solid var(--clr-blue-border)'
        }}>
          {/* Featured Image */}
          <div style={{
            width: '100%',
            background: 'linear-gradient(135deg, #1e293b, #0f172a)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            minHeight: '400px',
            position: 'relative'
          }}>
            {imageUrl && (
              <img
                src={imageUrl}
                alt={news.title || 'News image'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '550px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)'
                }}
                onError={(e) => {
                  // If image fails to load, use fallback
                  e.target.src = getFallbackImage();
                  e.target.style.maxHeight = '300px';
                }}
              />
            )}
            {!imageUrl && (
              <div style={{
                textAlign: 'center',
                color: 'rgba(255,255,255,0.5)',
                padding: '40px'
              }}>
                <i className="fas fa-newspaper" style={{ fontSize: '4rem', display: 'block', marginBottom: '16px' }}></i>
                <p style={{ fontSize: '1.2rem' }}>No image available</p>
              </div>
            )}
          </div>

          <div style={{ padding: '40px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
              {news.category && (
                <span style={{
                  padding: '6px 16px',
                  background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)',
                  color: 'white',
                  borderRadius: '30px',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}>{news.category}</span>
              )}
              {news.featured && (
                <span style={{
                  padding: '6px 16px',
                  background: 'var(--clr-amber)',
                  color: 'white',
                  borderRadius: '30px',
                  fontSize: '0.8rem',
                  fontWeight: 600
                }}><i className="fas fa-star"></i> Featured</span>
              )}
            </div>

            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', marginBottom: '20px' }}>{news.title}</h1>

            <div style={{ display: 'flex', gap: '25px', marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid var(--clr-blue-border)', flexWrap: 'wrap' }}>
              <span><i className="far fa-calendar-alt" style={{ color: 'var(--clr-blue)' }}></i> {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span><i className="far fa-clock" style={{ color: 'var(--clr-blue)' }}></i> {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>

            <div style={{ lineHeight: '1.9', color: 'var(--text-secondary)', fontSize: '1rem' }}>
              {news.description?.split('\n').map((p, i) => (
                <p key={i} style={{ marginBottom: '20px' }}>{p}</p>
              ))}
            </div>

            {news.pdfUrl && (
              <div style={{
                marginTop: '35px',
                paddingTop: '25px',
                borderTop: '2px solid var(--clr-blue-border)',
                background: 'rgba(14, 165, 233, 0.03)',
                borderRadius: '16px',
                padding: '25px'
              }}>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--clr-blue)' }}>
                  <i className="fas fa-paperclip"></i> Attachments
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'rgba(255, 255, 255, 0.7)',
                  border: '1px solid var(--clr-blue-border)',
                  borderRadius: '16px',
                  padding: '16px 20px',
                  flexWrap: 'wrap',
                  gap: '15px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                    <div style={{ width: '48px', height: '48px', background: 'rgba(16, 185, 129, 0.15)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', color: 'var(--clr-emerald)' }}>
                      <i className="fas fa-file-pdf"></i>
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{news.title} - Official Document</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>PDF Document</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <a href={news.pdfUrl} target="_blank" rel="noopener noreferrer" style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      borderRadius: '40px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)',
                      color: 'white',
                      transition: 'all 0.3s ease'
                    }}>
                      <i className="fas fa-eye"></i> View
                    </a>
                    <a href={news.pdfUrl} download style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      borderRadius: '40px',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      background: 'rgba(16, 185, 129, 0.15)',
                      border: '1px solid var(--clr-emerald)',
                      color: 'var(--clr-emerald)',
                      transition: 'all 0.3s ease'
                    }}>
                      <i className="fas fa-download"></i> Download
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Share buttons */}
            <div style={{ marginTop: '40px', paddingTop: '30px', borderTop: '1px solid var(--clr-blue-border)' }}>
              <h4 style={{ marginBottom: '15px' }}><i className="fas fa-share-alt"></i> Share this article</h4>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {[
                  { icon: 'facebook-f', color: '#1877f2', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
                  { icon: 'twitter', color: '#1da1f2', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(news.title)}&url=${encodeURIComponent(window.location.href)}` },
                  { icon: 'whatsapp', color: '#25D366', url: `https://wa.me/?text=${encodeURIComponent(`${news.title}\n\nRead more: ${window.location.href}`)}` },
                  { icon: 'linkedin-in', color: '#0077b5', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}` }
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      background: social.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.2rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className={`fab fa-${social.icon}`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related News */}
        {related.length > 0 && (
          <div style={{ marginTop: '60px' }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', marginBottom: '25px' }}>
              <i className="fas fa-newspaper" style={{ color: 'var(--clr-blue)' }}></i> Related News
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '24px'
            }}>
              {related.map(item => {
                const relatedImage = getImageUrl(item) || 'https://images.unsplash.com/photo-1562774053-701939374585?w=400';
                return (
                  <Link key={item.id} to={`/news/${item.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.5)',
                      backdropFilter: 'blur(20px) saturate(140%)',
                      borderRadius: '24px',
                      overflow: 'hidden',
                      border: '1px solid var(--clr-blue-border)',
                      transition: 'all 0.3s ease'
                    }}>
                      <img
                        src={relatedImage}
                        alt={item.title || 'News'}
                        style={{ width: '100%', height: '180px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1562774053-701939374585?w=400';
                        }}
                      />
                      <div style={{ padding: '20px' }}>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '8px' }}>{item.title || 'News'}</h3>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'Recent'}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default NewsDetail;